/**
 * DetailDrawer — reusable view/edit drawer for Engage entities.
 *
 * Slides in from the right when an Engage list-page row is clicked.
 * Renders record fields per the `fields` schema (view mode by default);
 * clicking "Edit" swaps to an inline form; Save posts to the detail
 * endpoint via the parent's onSave. Entity-specific one-click actions
 * (Convert / Complete / Mark Paid / ...) are passed as an `actions` array.
 *
 * Field types supported:
 *   text | textarea | email | mobile | number | date | datetime |
 *   select { options: [{value, label}] } | boolean | tags |
 *   readonly (view-only display, no edit input)
 *
 * Example use:
 *
 *   <DetailDrawer
 *     open={!!selected}
 *     onClose={() => setSelected(null)}
 *     record={selected}
 *     title={selected && selected.name}
 *     subtitle="Enquiry"
 *     fields={ENQUIRY_FIELDS}
 *     actions={[
 *       { label: 'Convert to Patient', onClick: (rec) => openConvert(rec) },
 *     ]}
 *     onSave={handleSave}
 *     onDelete={handleDelete}
 *   />
 */
import { useEffect, useState } from "react";
import { X, Edit3, Trash2, Save, ArrowLeft } from "lucide-react";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../ui/sheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select } from "antd";
import { ConfirmPopupModal } from "../ui/dialogModal";
import dayjs from "dayjs";

// ─── Field renderer (view mode) ─────────────────────────────────────────────

function renderView(field, value) {
    if (value === null || value === undefined || value === '') {
        return <span className="text-gray-400 italic">—</span>;
    }
    switch (field.type) {
        case 'boolean':
            return value
                ? <span className="inline-block px-2 py-0.5 text-xs rounded bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300 font-medium">Yes</span>
                : <span className="text-gray-400">No</span>;
        case 'tags':
            if (!Array.isArray(value) || value.length === 0) return <span className="text-gray-400 italic">—</span>;
            return (
                <div className="flex flex-wrap gap-1">
                    {value.map((t, i) => (
                        <span key={i} className="inline-block px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
                            {t}
                        </span>
                    ))}
                </div>
            );
        case 'datetime':
            return dayjs(value).format('DD MMM YYYY, HH:mm');
        case 'date':
            return dayjs(value).format('DD MMM YYYY');
        case 'select': {
            const opt = (field.options || []).find(o => o.value === value);
            return <span>{opt ? opt.label : value}</span>;
        }
        case 'textarea':
            return <div className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">{value}</div>;
        case 'email':
            return <a className="text-blue-600 hover:underline" href={`mailto:${value}`}>{value}</a>;
        case 'mobile':
            return <a className="text-blue-600 hover:underline" href={`tel:${value}`}>{value}</a>;
        default:
            return <span className="text-gray-900 dark:text-gray-100">{String(value)}</span>;
    }
}

// ─── Field editor (edit mode) ───────────────────────────────────────────────

function renderEdit(field, value, onChange) {
    switch (field.type) {
        case 'boolean':
            return (
                <input
                    type="checkbox"
                    checked={!!value}
                    onChange={(e) => onChange(e.target.checked)}
                    className="rounded"
                />
            );
        case 'select':
            return (
                <Select
                    className="w-full"
                    value={value ?? ''}
                    onChange={(v) => onChange(v)}
                    options={field.options || []}
                    allowClear
                />
            );
        case 'textarea':
            return (
                <textarea
                    className="w-full border border-gray-300 dark:border-gray-700 rounded p-2 text-sm bg-white dark:bg-gray-900"
                    rows={3}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                />
            );
        case 'tags':
            return (
                <Input
                    value={Array.isArray(value) ? value.join(', ') : (value || '')}
                    onChange={(e) => {
                        const raw = e.target.value;
                        const arr = raw.split(',').map(s => s.trim()).filter(Boolean);
                        onChange(arr);
                    }}
                    placeholder="comma-separated"
                />
            );
        case 'date':
        case 'datetime':
            return (
                <Input
                    type={field.type === 'date' ? 'date' : 'datetime-local'}
                    value={value ? String(value).slice(0, field.type === 'date' ? 10 : 16) : ''}
                    onChange={(e) => onChange(e.target.value)}
                />
            );
        case 'number':
            return (
                <Input
                    type="number"
                    step={field.step || 'any'}
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                />
            );
        case 'readonly':
            return renderView(field, value);
        default:
            return (
                <Input
                    type={field.type === 'email' ? 'email' : 'text'}
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                />
            );
    }
}

// ─── Main component ────────────────────────────────────────────────────────

export function DetailDrawer({
    open,
    onClose,
    record,
    title,
    subtitle,
    fields = [],
    actions = [],         // [{ label, onClick(record), variant?, hidden?(record) }]
    onSave,                // async (patch) → any
    onDelete,              // async (record) → any
    editable = true,
    deletable = true,
}) {
    const [editMode, setEditMode] = useState(false);
    const [draft, setDraft] = useState({});
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    // Reset state when record changes / drawer closes
    useEffect(() => {
        setDraft(record || {});
        setEditMode(false);
    }, [record, open]);

    if (!open || !record) return null;

    const setField = (key, value) => {
        setDraft(prev => ({ ...prev, [key]: value }));
    };

    const handleSave = async () => {
        if (!onSave) return;
        setSaving(true);
        try {
            // Only send editable fields (not readonly ones like id, timestamps, computed)
            const payload = {};
            for (const f of fields) {
                if (f.type === 'readonly') continue;
                if (f.key in draft) payload[f.key] = draft[f.key];
            }
            await onSave(payload, record);
            setEditMode(false);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setConfirmDelete(false);
        if (!onDelete) return;
        await onDelete(record);
        onClose();
    };

    return (
        <>
            <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
                <SheetContent side="right" className="w-full sm:max-w-lg md:max-w-xl overflow-y-auto">
                    {/* Header */}
                    <SheetHeader className="border-b border-gray-100 dark:border-gray-800 pb-3 mb-4">
                        <div className="flex items-start justify-between">
                            <div className="min-w-0 pr-4">
                                {subtitle && (
                                    <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{subtitle}</div>
                                )}
                                <SheetTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                                    {title || `Record #${record.id}`}
                                </SheetTitle>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                                aria-label="Close"
                            >
                                <X className="w-4 h-4 text-gray-500" />
                            </button>
                        </div>
                    </SheetHeader>

                    {/* Actions strip */}
                    {(actions.length > 0 || editable || deletable) && (
                        <div className="flex flex-wrap gap-2 mb-4">
                            {actions
                                .filter(a => !a.hidden || !a.hidden(record))
                                .map((a, i) => (
                                    <Button
                                        key={i}
                                        size="sm"
                                        variant={a.variant || 'outline'}
                                        onClick={() => a.onClick(record)}
                                    >
                                        {a.icon && <a.icon className="w-3 h-3 mr-1" />}
                                        {a.label}
                                    </Button>
                                ))
                            }
                            {editable && !editMode && (
                                <Button size="sm" variant="outline" onClick={() => setEditMode(true)}>
                                    <Edit3 className="w-3 h-3 mr-1" /> Edit
                                </Button>
                            )}
                            {editMode && (
                                <>
                                    <Button size="sm" variant="ghost" onClick={() => { setDraft(record); setEditMode(false); }}>
                                        <ArrowLeft className="w-3 h-3 mr-1" /> Cancel
                                    </Button>
                                    <Button size="sm" onClick={handleSave} disabled={saving}>
                                        <Save className="w-3 h-3 mr-1" /> {saving ? 'Saving…' : 'Save'}
                                    </Button>
                                </>
                            )}
                            {deletable && !editMode && (
                                <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)}>
                                    <Trash2 className="w-3 h-3 mr-1 text-rose-600" />
                                    <span className="text-rose-600">Delete</span>
                                </Button>
                            )}
                        </div>
                    )}

                    {/* Field list */}
                    <div className="space-y-3">
                        {fields.map((f) => (
                            <div key={f.key} className="grid grid-cols-3 gap-3 items-start text-sm py-1">
                                <div className="col-span-1 text-gray-500 dark:text-gray-400 pt-1">{f.label}</div>
                                <div className="col-span-2">
                                    {editMode && f.type !== 'readonly'
                                        ? renderEdit(f, draft[f.key], (v) => setField(f.key, v))
                                        : renderView(f, record[f.key])}
                                </div>
                            </div>
                        ))}
                    </div>
                </SheetContent>
            </Sheet>

            <ConfirmPopupModal
                open={confirmDelete}
                onClose={() => setConfirmDelete(false)}
                onConfirm={handleDelete}
                title="Delete record?"
                description="This will soft-delete the record. It can be restored later by an admin."
                confirmLabel="Delete"
                type="danger"
                destructive
            />
        </>
    );
}
